import React, { Fragment } from 'react';
import { isEmpty, orderBy, sortBy, pick, omit, get, keyBy } from 'lodash';
import { format as formatDate } from 'date-fns';
import { Button } from 'reactstrap';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import dedent from 'dedent';

import { canUpdateMethodInquiry, canApproveMethodInquiry } from '../../shared/abilities';
import { answerDisplayValue, } from '../../util';
import { MethodInquiryDetailRows } from '../InquiryDetailTable';
import firebase, { functions } from '../../firebase';
import {
  statuses,
  fields as _fields,
  basicFields,
  itemFields,
  shipmentFields,
  commentFields,
  activityTypes,
  replyEmailBody,
  shipmentMailBody,
  shippingDestination,
  supportContentsFields,
  emailFields,
} from '../../shared/models/methodInquiry';
import { paymentMethod, ordererFields, destinationFields } from '../../shared/models/order';
import { fieldDisplayValue } from '../../shared/util';
import { downloadInquiryPdf, buildInquiryPdfHtml, sanitizePdfHtml, inquiryPdfOptions, translatablePdfEmailFields, PdfEmailConfirmBody, pdfTranslationLanguages, toPdfAttachedFiles } from '../../util';
import { supportMeans } from '../../shared/config';
import AdminPage from '../hocs/AdminPage';
import useTenant from '../hooks/useTenant';
import { InquiryShipmentButton } from '../InquiryShipmentButton';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCreateActivity from '../hooks/useCreateActivity';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import InquiryItemsFormModal from '../modals/InquiryItemsFormModal';
import ModelFormModal from '../modals/ModelFormModal';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import { InquiryUpdatePicButton } from '../InquiryUpdatePicButton';
import InquirySubAccountAlert from '../InquirySubAccountAlert';
import InquiryReinquiryAlert from '../InquiryReinquiryAlert';
import ModalButton from '../ModalButton';
import Activity from '../Activity';
import TenantLink from '../TenantLink';
import ShareInquiryButton from '../ShareInquiryButton';

const db = firebase.firestore();
const methodInquiriesRef = db.collection('methodInquiries');
const productTypesRef = db.collection('productTypes');
const productsRef = db.collection('products');
const activitiesRef = db.collection('activities');
const ordersRef = db.collection('orders');
const surveysRef = db.collection('surveys');
const usersRef = db.collection('users');
const questionsRef = db.collection('questions');
const storageRef = firebase.storage().ref();
const notifyInquiryResponser = functions.httpsCallable('notifyInquiryResponser');
const sendMethodInquiryEmail = functions.httpsCallable('sendMethodInquiryEmail');
const sendInquiryPdfEmail = functions.httpsCallable('sendInquiryPdfEmail');
const methodInquiryShipment = functions.httpsCallable('methodInquiryShipment');
const { entries, keys } = Object;

export default AdminPage(function AdminMethodInquiry(props) {
  const {
    user,
    match: {
      params: { tenantPath, methodInquiryId },
    },
  } = props;
  const createActivity = useCreateActivity();
  const tenant = useTenant();
  const productTypes = useCollectionSubscriptionInTenant(productTypesRef.orderBy('index'));
  const products = useCollectionSubscriptionInTenant(productsRef.orderBy('createdAt'));
  const sortedProducts = sortBy(products, ({ isHidden }) => (isHidden ? 1 : 0));
  const selectableProducts = sortedProducts.filter(_ => !_.isBody).map(_ => ({
    ..._,
    label: `${_.isOut ? '[廃盤] ' : ''}${_.isHidden ? '[非表示] ' : ''}[${_.code}] ${_.name}`,
  }));
  const productsById = keyBy(products, 'id');
  const fields = { ...omit(_fields({ productTypes, products }), ['destinationType']), ...shipmentFields(), };
  const methodInquiry = useDocumentSubscription(methodInquiriesRef.doc(methodInquiryId), [methodInquiryId]);
  const tenantUser = useDocumentSubscription(
    methodInquiry?.createdBy?.uid && tenant?.ref.collection('tenantUsers').doc(methodInquiry.createdBy.uid),
    [methodInquiry?.createdBy?.uid, tenant?.id]
  );
  const currentAssignee = useDocumentSubscription(
    tenantUser?.assigneeId && db.collection('users').doc(tenantUser.assigneeId),
    [tenantUser?.assigneeId]
  );
  const childMethodInquiries = useCollectionSubscriptionInTenant(methodInquiriesRef.where('sourceMethodInquiryId', '==', methodInquiryId), [methodInquiryId]);
  const commentsRef = methodInquiriesRef.doc(methodInquiryId).collection('methodInquiryComments');
  const comments = useCollectionSubscription(commentsRef.orderBy('createdAt', 'desc'), [methodInquiryId]);
  const activities = useCollectionSubscriptionInTenant(
    activitiesRef.where('payload.methodInquiryId', '==', methodInquiryId),
    [methodInquiryId]
  );
  const shippable =
    methodInquiry &&
    !methodInquiry.shipmentRequested &&
    ['name', 'nameKana', 'phone', 'postalCode', 'prefecture', 'city', 'address', 'email'].every(_ => methodInquiry[_]) &&
    methodInquiry.items?.every((_) => _.quantity <= get(productsById, [_.productId, 'normalInventory'], 0));
  const shipmentOrderId = methodInquiry?.orderId;
  const shipmentOrder = useDocumentSubscription(shipmentOrderId && db.collection('orders').doc(shipmentOrderId), [shipmentOrderId]);
  const sourceOrderId = methodInquiry?.sourceOrder?.id;
  const sourceOrder = useDocumentSubscription(sourceOrderId && ordersRef.doc(sourceOrderId), [sourceOrderId]);
  const thWidth = 220;
  const surveys = useCollectionSubscriptionInTenant(surveysRef);
  const surveysById = keyBy(surveys, 'id');
  const isReinquiry = methodInquiry?.sourceTroubleInquiryId != null;
  const methodInquirySurvey = surveysById[productsById[methodInquiry?.productId]?.[isReinquiry ? 'methodReinquirySurveyId' : 'methodInquirySurveyId']];
  const relatedQuestions = useDocumentsFetch((methodInquirySurvey?.questionRows?.map(_ => _.questionId) || []).map(_ => questionsRef.doc(_)), [methodInquirySurvey]);
  const onClickStart = async () => {
    if (!window.confirm('本当に対応開始しますか？')) return;

    const currentAssigneeId = tenantUser?.assigneeId;
    const isAssigneeChanged = currentAssigneeId !== user.uid;
    
    if (isAssigneeChanged) {
      const currentAssigneeName = currentAssignee?.displayName || '設定なし';
      const confirmMessage = `このアカウントの担当者を${currentAssigneeName}からあなたに変更しますか？`;

      if (window.confirm(confirmMessage)) {
        await tenant.ref.collection('tenantUsers').doc(methodInquiry.createdBy.uid).update({ assigneeId: user.uid });
      }
    }

    await methodInquiry.ref.update({ status: 'supporting', respondedBy: user });
    await createActivity('startSupportOfMethodInquiry', user, { methodInquiryId, uid: methodInquiry.createdBy.uid });
  };
  const onClickUnfinish = async () => {
    if (!window.confirm('本当に対応中に戻しますか？')) return;

    const status =
      methodInquiry.hasReimbursement && !methodInquiry.reimbursementRequest?.requestedAt ? 'awaiting' : 'supporting';
    await methodInquiry.ref.update({ status });
    await createActivity('unfinishSupportOfMethodInquiry', user, { methodInquiryId, uid: methodInquiry.createdBy.uid });
  };
  const validateOnDone = async () => {
    if (!window.confirm('本当に対応完了しますか？')) return false;
    return true;
  };
  const onDoneFinished = async (values) => {
    const note = `${values.supportSummary} (${values.supportMeans.map((_) => supportMeans[_]).join(',')})`;
    await methodInquiry.ref.update({ status: 'supported' });
    await createActivity('finishSupportOfMethodInquiry', user, { methodInquiryId, note, uid: methodInquiry.createdBy.uid });
  };
  const onSubmitUpdatePic = async (respondedBy) => {
    const currentAssigneeId = tenantUser?.assigneeId;
    const isAssigneeChanged = currentAssigneeId !== respondedBy.uid;
    
    if (isAssigneeChanged) {
      const currentAssigneeName = currentAssignee?.displayName || '設定なし';
      const confirmMessage = `このアカウントの担当者を${currentAssigneeName}から${respondedBy.displayName}に変更しますか？`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
      await tenant.ref.collection('tenantUsers').doc(methodInquiry.createdBy.uid).update({ assigneeId: respondedBy.uid });
    }

    await createActivity('updatePicOfMethodInquiry', user, { methodInquiryId, uid: methodInquiry.createdBy.uid });
    await notifyInquiryResponser({ tenantId: tenantPath, modelName: 'methodInquiry', modelId: methodInquiryId, respondedBy });
  };
  const onSubmitEmail = async (values, { onClickClose, }) => {
    try {
      const attachedFiles = await Promise.all(
        Array.from(values.files || []).map(async (file) => {
          const storagePath = `methodInquiries/${methodInquiryId}/attachedFiles/${new Date().toISOString()}/${file.name}`;
          const fileRef = storageRef.child(storagePath);
          await fileRef.put(file, { contentType: file.type });
          return {
            ...pick(file, ['name', 'type']),
            storagePath,
            url: await fileRef.getDownloadURL(),
          };
        })
      );
      const { data: sendResult } = await sendMethodInquiryEmail({ methodInquiryId: methodInquiry.id, values, });
      await methodInquiry.ref.update({
        messages: firebase.firestore.FieldValue.arrayUnion({ ...omit(values, 'files'), attachedFiles, sentAt: new Date(), sentBy: pick(user, ['email', 'uid', 'displayName']), }),
      });
      const body = replyEmailBody(methodInquiry, values.body, products, productTypes, relatedQuestions, window.location.origin)
      await createActivity('sendMethodInquiryEmail', user, { methodInquiryId, uid: methodInquiry.createdBy.uid, body, messageBody: values.body, attachedFiles, ...sendResult });
      toast.success('送信しました');
      onClickClose();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const onShipmentFinished = async () => {
    await createActivity('createOrderOfMethodInquiry', user, {
      methodInquiryId,
      uid: methodInquiry.createdBy?.uid || null,
    });
  };
  const onEdited = async ({ items }) => {
    await createActivity('editMethodInquiry', user, {
      methodInquiryId,
      uid: methodInquiry.createdBy?.uid || null,
    });
  };
  const onEditedDestination = async () => {
    await createActivity('editDestinationOfMethodInquiry', user, {
      methodInquiryId,
      uid: methodInquiry.createdBy?.uid || null,
    });
  };
  const mailConfirmBody = ({ values }) => {
    const body = replyEmailBody(methodInquiry, values.body, products, productTypes, relatedQuestions, window.location.origin)
    return (
      <div>
        <div>以下のようなメールを送ります。よろしいですか？</div>
        <div className="card p-3 mt-3 text-break" style={{ whiteSpace: 'pre-line' }}>
          {body}
        </div>
        <div className="mt-4">
          <h5>メッセージ内容</h5>
          <div className="card p-3 text-break" style={{ whiteSpace: 'pre-line', }}>
            {values.body}
          </div>
        </div>
        {values.files && values.files.length > 0 && (
          <div className="mt-4">
            <h5>添付ファイル</h5>
            <div className="card p-3">
              {Array.from(values.files).map((file, index) => {
                const fileUrl = URL.createObjectURL(file);
                return (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <i className="fas fa-paperclip mr-2"></i>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                      download={file.name}
                    >
                      {file.name}
                    </a>
                    <small className="text-muted ml-2">({numeral(file.size).format('0.0 b')})</small>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    methodInquiry != null && (
      <div className='admin-method-inquiry container-fluid'>
        <div className='p-4 bg-white my-4'>
          <div className='d-flex justify-content-center mb-3'>
            <h4>乗り方・使い方問合せ詳細</h4>
          </div>
          <InquirySubAccountAlert inquiry={methodInquiry} />
          <InquiryReinquiryAlert inquiry={methodInquiry} />
          <div className='d-flex flex-wrap text-nowrap mb-3 gap-2'>
            {methodInquiry.status === 'initial' && (
              <Button color='primary' onClick={onClickStart}>
                対応開始
              </Button>
            )}
            {methodInquiry.status !== 'initial' && (
              <ModalButton Modal={ModelFormModal} modalProps={{ title: 'メール送信', fields: emailFields(), submitLabel: '送信する', onSubmit: onSubmitEmail, confirms: true, confirmBody: mailConfirmBody, }}>
                <span className="fas fa-paper-plane mr-1" /> メール送信
              </ModalButton>
            )}
            {['supporting', 'awaiting'].includes(methodInquiry.status) && (
              <Fragment>
                <EditButton
                  itemRef={methodInquiry.ref}
                  FormModal={InquiryItemsFormModal}
                  disabled={!canUpdateMethodInquiry(user)}
                  formProps={{ products: selectableProducts, itemFields }}
                  onFinish={onEdited}
                />
                <EditButton
                  label="発送先変更"
                  icon={null}
                  itemRef={methodInquiry.ref}
                  FormModal={ModelFormModal}
                  formProps={{
                    title: '発送先を変更する',
                    fields: destinationFields,
                    submitLabel: '変更する',
                    values: shippingDestination(methodInquiry),
                  }}
                  disabled={!canUpdateMethodInquiry(user)}
                  onFinish={onEditedDestination}
                />
                <InquiryShipmentButton
                  inquiryRef={methodInquiry.ref}
                  inquiry={methodInquiry}
                  user={user}
                  disabled={!shippable}
                  products={products}
                  productTypes={productTypes}
                  shipmentFields={shipmentFields}
                  shipmentMailBody={shipmentMailBody}
                  onFinish={onShipmentFinished}
                  inquiryShipment={methodInquiryShipment}
                  shipmentOrder={shipmentOrder}
                />
                <EditButton
                  label='対応完了'
                  icon={null}
                  color='primary'
                  itemRef={methodInquiry.ref}
                  FormModal={ModelFormModal}
                  formProps={{
                    title: 'サポート内容を登録する',
                    fields: supportContentsFields({ surveys }),
                    submitLabel: '登録する',
                  }}
                  validateOnSubmit={validateOnDone}
                  onFinish={onDoneFinished}
                />
              </Fragment>
            )}
            {methodInquiry.status !== 'supported' && (
              <InquiryUpdatePicButton inquiry={methodInquiry} onSubmit={onSubmitUpdatePic} />
            )}
            {methodInquiry.status === 'supported' && (
              <Button color='primary' outline onClick={onClickUnfinish}>
                対応中に戻す
              </Button>
            )}
            <ModalButton
              Modal={ModelFormModal}
              modalProps={{
                title: 'PDF送信',
                fields: translatablePdfEmailFields,
                values: { subject: `乗り方使い方問合せ_${methodInquiry.id}.pdf` },
                submitLabel: '送信',
                confirms: true,
                confirmBody: PdfEmailConfirmBody,
                onSubmit: async (values, { onClickClose }) => {
                  const pdfFileName = `乗り方使い方問合せ_${methodInquiry.id}.pdf`;
                  const html = sanitizePdfHtml(buildInquiryPdfHtml('.inquiry-detail-table', { hidePersonalInfo: !values.includePersonalInfo }));
                  const storagePath = `methodInquiries/${methodInquiryId}/pdfEmails/${new Date().toISOString()}/${pdfFileName}`;
                  const { data } = await sendInquiryPdfEmail({ tenantId: tenant.id, to: values.to, subject: values.subject, body: values.body, html, storagePath, pdfFileName, languages: pdfTranslationLanguages(values), pdfOptions: inquiryPdfOptions });
                  const attachedFiles = toPdfAttachedFiles(data);
                  await createActivity('sendMethodInquiryPdfEmail', user, { methodInquiryId, uid: methodInquiry.createdBy.uid, body: `送信先: ${values.to}\n\n${values.body || ''}`.trim(), messageBody: values.body, attachedFiles });
                  toast.success('送信しました');
                  onClickClose();
                },
              }}
            >
              <span className="fas fa-file-pdf mr-1" />PDF送信
            </ModalButton>
            <ShareInquiryButton inquiry={methodInquiry} />
          </div>
          <div className="d-sm-flex justify-content-around mt-5 gap-4">
            <div className="flex-fill inquiry-detail-table">
              <table className='table table-bordered'>
                <tbody className='thead-light'>
                  <MethodInquiryDetailRows
                    inquiry={methodInquiry}
                    productsById={productsById}
                    productTypes={productTypes}
                    products={products}
                    fields={fields}
                    country={undefined}
                    relatedQuestions={relatedQuestions}
                    renderNameValue={_ => <TenantLink to={`/admin/users/${methodInquiry.createdBy.uid}`}>{_}</TenantLink>}
                    extraRows={
                      <tr>
                        <th style={{ width: thWidth }}>アカウント担当</th>
                        <td>{currentAssignee?.displayName || '設定なし'}</td>
                      </tr>
                    }
                  />
                </tbody>
              </table>
            </div>
            <div className="mt-5 mt-sm-0" style={{ minWidth: '40vw', maxWidth: window.innerWidth >= 576 ? '50vw' : 'none' }}>
              {
                !isEmpty(childMethodInquiries) && (
                  <div>
                    <h5>再問合せ</h5>
                    <div className="overflow-auto" style={{ maxHeight: 360, }}>
                      {
                        orderBy(childMethodInquiries, _ => _.createdAt.toDate(), 'desc').map((childMethodInquiry) => {
                          const { id, createdAt, } = childMethodInquiry;
                          return (
                            <div key={id} className="d-flex justify-content-between">
                              <TenantLink to={`/admin/methodInquiries/${id}`}>{id}</TenantLink>
                              <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )
              }
              <hr className='my-5' />
              <div>
                <div className='d-flex justify-content-end mb-3'>
                  <AddInTenantButton
                    label='新規コメント'
                    itemRef={commentsRef.doc()}
                    processValues={(_) => ({ ..._, createdBy: pick(user, ['uid', 'email', 'displayName']) })}
                    FormModal={ModelFormModal}
                    formProps={{ title: '新規コメント', fields: commentFields }}
                  />
                </div>
                <div className="overflow-auto" style={{ maxHeight: 360, }}>
                  {comments.length > 0 ? (
                    <div>
                      {comments.map(({ id, body, createdAt, createdBy }) => {
                        return (
                          <div key={id} className='card p-3 mb-3'>
                            <div className='small text-muted mb-1'>
                              {createdBy.displayName} {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                            </div>
                            <div className="text-break" style={{ whiteSpace: 'pre-line' }}>{body}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div>コメントはまだありません</div>
                  )}
                </div>
              </div>
              <hr className='my-5' />
              <div>
                <h5>アクティビティ</h5>
                <div className="overflow-auto" style={{ maxHeight: 360, }}>
                  {activities.length > 0 ? (
                    <div className='d-flex flex-column gap-1'>
                      {orderBy(activities, (_) => _.createdAt.toDate(), 'desc').map((_) => (
                        <Activity activity={_} activityTypes={activityTypes} />
                      ))}
                    </div>
                  ) : (
                    <div>アクティビティはまだありません</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
});
